<template>
  <div class="mqtt-container">
    <!-- MQTT Configuration Section -->
    <div class="mqtt-section">
      <h3 class="mqtt-title">MQTT Configuration</h3>
      <div class="form-row">
        <div class="form-group">
          <label>Protocol:</label>
          <select v-model="connection.protocol" @change="handleProtocolChange">
            <option value="ws">ws://</option>
            <option value="wss">wss://</option>
          </select>
        </div>
        <div class="form-group">
          <label>Host:</label>
          <input v-model="connection.host" />
        </div>
        <div class="form-group">
          <label>Port:</label>
          <input v-model.number="connection.port" type="number" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-group">
          <label>Client ID:</label>
          <input v-model="connection.clientId" />
        </div>
        <div class="form-group">
          <label>Username:</label>
          <input v-model="connection.username" />
        </div>
        <div class="form-group">
          <label>Password:</label>
          <input v-model="connection.password" type="password" />
        </div>
      </div>
      <div class="form-row">
        <button 
          class="btn btn-success" 
          :disabled="client.connected || connecting"
          @click="createConnection"
        >
          {{ connecting ? 'Connecting...' : (client.connected ? 'Connected' : 'Connect') }}
        </button>
        <button 
          v-if="client.connected" 
          class="btn btn-danger" 
          @click="destroyConnection"
        >
          Disconnect
        </button>
      </div>
      <div class="connection-status" :class="{ connected: client.connected, disconnected: !client.connected }">
        Status: {{ client.connected ? 'Connected' : 'Disconnected' }}
      </div>
      <div v-if="connectionError" class="connection-error">
        Error: {{ connectionError }}
      </div>
    </div>

    <!-- Subscribe Section -->
    <div class="mqtt-section">
      <h3 class="mqtt-title">Subscribe</h3>
      <div class="form-row">
        <div class="form-group">
          <label>Topic:</label>
          <input v-model="subscription.topic" />
        </div>
        <div class="form-group">
          <label>QoS:</label>
          <select v-model="subscription.qos">
            <option v-for="qos in qosList" :key="qos" :value="qos">{{ qos }}</option>
          </select>
        </div>
      </div>
      <div class="form-row">
        <button 
          class="btn btn-success" 
          :disabled="!client.connected || !clientReady"
          @click="doSubscribe"
        >
          {{ subscribeSuccess ? 'Subscribed' : 'Subscribe' }}
        </button>
        <button 
          v-if="subscribeSuccess" 
          class="btn btn-warning" 
          :disabled="!client.connected"
          @click="doUnSubscribe"
        >
          Unsubscribe
        </button>
      </div>
    </div>

    <!-- Real-time Monitoring Section -->
    <div class="mqtt-section">
      <h3 class="mqtt-title">
        实时状态监控 
        <span v-if="latestData" class="live-indicator">●</span>
      </h3>
      <div v-if="latestData" class="realtime-panel">
        <div class="panel-row">
          <div class="panel-item">
            <div class="item-label">车辆ID</div>
            <div class="item-value">{{ latestData.carId }}</div>
          </div>
          <div class="panel-item">
            <div class="item-label">学员ID</div>
            <div class="item-value">{{ latestData.studentId }}</div>
          </div>
          <div class="panel-item">
            <div class="item-label">速度</div>
            <div class="item-value speed">{{ formatNumber(latestData.speed) }} km/h</div>
          </div>
          <div class="panel-item">
            <div class="item-label">挡位</div>
            <div class="item-value">{{ latestData.gear }}</div>
          </div>
        </div>
        <div class="panel-row">
          <div class="panel-item">
            <div class="item-label">转速</div>
            <div class="item-value">{{ latestData.rmp }} RPM</div>
          </div>
          <div class="panel-item">
            <div class="item-label">方向盘</div>
            <div class="item-value">{{ formatNumber(latestData.steerWheelAngle) }}°</div>
          </div>
          <div class="panel-item">
            <div class="item-label">主动安全</div>
            <div :class="['item-value', 'status-' + latestData.unBreak]">{{ getSafetyStatus(latestData.unBreak) }}</div>
          </div>
          <div class="panel-item">
            <div class="item-label">安全带</div>
            <div :class="['item-value', 'status-' + latestData.belt]">{{ getBeltStatus(latestData.belt) }}</div>
          </div>
        </div>
        <div class="panel-row">
          <div class="panel-item">
            <div class="item-label">油门</div>
            <div class="item-value">{{ formatNumber(latestData.oil) }}%</div>
          </div>
          <div class="panel-item">
            <div class="item-label">脚刹</div>
            <div class="item-value">{{ formatNumber(latestData.break) }}%</div>
          </div>
          <div class="panel-item">
            <div class="item-label">离合</div>
            <div class="item-value">{{ formatNumber(latestData.cluth) }}%</div>
          </div>
          <div class="panel-item">
            <div class="item-label">手刹</div>
            <div :class="['item-value', 'status-' + latestData.handBreak]">{{ getHandBreakStatus(latestData.handBreak) }}</div>
          </div>
        </div>
      </div>
      <div v-else class="empty-message">
        等待接收数据...
      </div>
    </div>

    <!-- Received Messages Section -->
    <div class="mqtt-section">
      <h3 class="mqtt-title">Received Messages</h3>
      <div class="receive-container">
        <div class="receive-messages">
          <div v-for="(msg, index) in receivedMessages" :key="index" class="message-item">
            <div class="msg-header">
              <span class="msg-time">{{ msg.time }}</span>
              <span class="msg-topic">{{ msg.topic }}</span>
            </div>
            <div v-if="msg.parsedData" class="msg-parsed">
              <table class="signal-table">
                <tr><th colspan="2" class="table-header">车辆信息</th></tr>
                <tr><td class="label">车辆ID</td><td>{{ msg.parsedData.carId || '-' }}</td></tr>
                <tr><td class="label">学员ID</td><td>{{ msg.parsedData.studentId || '-' }}</td></tr>
                <tr><td class="label">训练记录ID</td><td>{{ msg.parsedData.practiseId || '-' }}</td></tr>
                <tr><td class="label">时间戳</td><td>{{ formatTimestamp(msg.parsedData.timestamp) }}</td></tr>

                <tr><th colspan="2" class="table-header">位置信息</th></tr>
                <tr><td class="label">经度</td><td>{{ msg.parsedData.longtitude || '-' }}</td></tr>
                <tr><td class="label">纬度</td><td>{{ msg.parsedData.latitude || '-' }}</td></tr>
                <tr><td class="label">高度</td><td>{{ msg.parsedData.height || '-' }} m</td></tr>
                <tr><td class="label">东北天坐标(东)</td><td>{{ msg.parsedData.x || '-' }}</td></tr>
                <tr><td class="label">东北天坐标(北)</td><td>{{ msg.parsedData.y || '-' }}</td></tr>
                <tr><td class="label">东北天坐标(天)</td><td>{{ msg.parsedData.z || '-' }}</td></tr>

                <tr><th colspan="2" class="table-header">姿态信息</th></tr>
                <tr><td class="label">方位角</td><td>{{ msg.parsedData.azimuth || '-' }}°</td></tr>
                <tr><td class="label">俯仰角</td><td>{{ msg.parsedData.pitchAngle || '-' }}°</td></tr>
                <tr><td class="label">横滚角</td><td>{{ msg.parsedData.roll || '-' }}°</td></tr>
                <tr><td class="label">方向盘角度</td><td>{{ msg.parsedData.steerWheelAngle || '-' }}°</td></tr>

                <tr><th colspan="2" class="table-header">行驶信息</th></tr>
                <tr><td class="label">速度</td><td>{{ msg.parsedData.speed || '-' }} km/h</td></tr>
                <tr><td class="label">转速</td><td>{{ msg.parsedData.rmp || '-' }} RPM</td></tr>
                <tr><td class="label">挡位</td><td>{{ msg.parsedData.gear || '-' }}</td></tr>
                <tr><td class="label">总里程</td><td>{{ msg.parsedData.totalMileage || '-' }} km</td></tr>

                <tr><th colspan="2" class="table-header">踏板信息</th></tr>
                <tr><td class="label">离合</td><td>{{ msg.parsedData.cluth || '-' }}%</td></tr>
                <tr><td class="label">油门</td><td>{{ msg.parsedData.oil || '-' }}%</td></tr>
                <tr><td class="label">脚刹</td><td>{{ msg.parsedData.break || '-' }}%</td></tr>
                <tr><td class="label">副脚刹</td><td>{{ msg.parsedData.subBreak || '-' }}%</td></tr>

                <tr><th colspan="2" class="table-header">安全状态</th></tr>
                <tr><td class="label">主动安全状态</td><td>{{ getSafetyStatus(msg.parsedData.unBreak) }}</td></tr>
                <tr><td class="label">手刹</td><td>{{ getHandBreakStatus(msg.parsedData.handBreak) }}</td></tr>
                <tr><td class="label">安全带</td><td>{{ getBeltStatus(msg.parsedData.belt) }}</td></tr>

                <tr><th colspan="2" class="table-header">灯光状态</th></tr>
                <tr><td class="label">左方向灯</td><td>{{ getLightStatus(msg.parsedData.leftLight) }}</td></tr>
                <tr><td class="label">右方向灯</td><td>{{ getLightStatus(msg.parsedData.rightLight) }}</td></tr>
                <tr><td class="label">近光灯</td><td>{{ getLightStatus(msg.parsedData.nearLight) }}</td></tr>
                <tr><td class="label">远光灯</td><td>{{ getLightStatus(msg.parsedData.farLight) }}</td></tr>
                <tr><td class="label">示廓灯</td><td>{{ getLightStatus(msg.parsedData.widthLight) }}</td></tr>
                <tr><td class="label">危险警告灯</td><td>{{ getLightStatus(msg.parsedData.dangerLight) }}</td></tr>
                <tr><td class="label">雾灯</td><td>{{ getLightStatus(msg.parsedData.flogLight) }}</td></tr>

                <tr><th colspan="2" class="table-header">其他信息</th></tr>
                <tr><td class="label">车门状态</td><td>{{ getDoorStatus(msg.parsedData.door) }}</td></tr>
                <tr><td class="label">车外按钮</td><td>{{ msg.parsedData.button || '-' }}</td></tr>
                <tr><td class="label">平均油耗</td><td>{{ msg.parsedData.averageOil || '-' }} L/100km</td></tr>
                <tr><td class="label">剩余油量</td><td>{{ msg.parsedData.leftOil || '-' }} L</td></tr>
              </table>
            </div>
            <div v-else class="msg-payload">
              {{ msg.payload }}
            </div>
          </div>
          <div v-if="receivedMessages.length === 0" class="empty-message">
            Waiting for messages...
          </div>
        </div>
      </div>
    </div>

    <!-- Publish Section -->
    <div class="mqtt-section">
      <h3 class="mqtt-title">Publish Test Message</h3>
      <div class="form-row">
        <div class="form-group">
          <label>Topic:</label>
          <input v-model="publish.topic" />
        </div>
        <div class="form-group">
          <label>QoS:</label>
          <select v-model="publish.qos">
            <option v-for="qos in qosList" :key="qos" :value="qos">{{ qos }}</option>
          </select>
        </div>
      </div>
      <div class="form-row">
        <div class="form-group full-width">
          <label>Payload (JSON):</label>
          <textarea v-model="publish.payload" rows="6"></textarea>
        </div>
      </div>
      <button 
        class="btn btn-primary" 
        :disabled="!client.connected || !clientReady"
        @click="doPublish"
      >
        Publish
      </button>
    </div>
  </div>
</template>

<script>
import mqtt from 'mqtt'
import EventBus from '@/utils/eventBus'

export default {
  name: 'MQTT',
  mounted() {
    EventBus.$on('carIdUpdate', this.handleCarIdUpdate);
    EventBus.$on('carStatus', this.handleCarStatus);
    if (this.carStatus) {
      this.scheduleAutoConnect();
    }
  },
  beforeDestroy() {
    EventBus.$off('carIdUpdate', this.handleCarIdUpdate);
    EventBus.$off('carStatus', this.handleCarStatus);
    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
  },
  data() {
    return {
      carId: null,
      carStatus: false,
      connection: {
        protocol: 'wss',
        host: 'stulive.xihaxueche.com',
        port: 8071,
        endpoint: '/mqtt',
        clean: true,
        connectTimeout: 30 * 1000,
        reconnectPeriod: 4000,
        clientId: 'springboot-mqtt-consumer-dev-' + Date.now(),
        username: 'admin',
        password: 'ghgd.123'
      },
      subscription: {
        topic: 'vehicle/+/up/#',
        qos: 0
      },
      publish: {
        topic: 'vehicle/test/up/signal',
        qos: 0,
        payload: JSON.stringify({
          carId: 1001,
          studentId: 2001,
          timestamp: Date.now(),
          practiseId: 3001,
          x: 123.45,
          y: 678.90,
          z: 10.5,
          longtitude: 116.397428,
          latitude: 39.90923,
          height: 45.6,
          azimuth: 90.0,
          pitchAngle: 0.5,
          roll: 0.2,
          speed: 30.5,
          cluth: 0,
          oil: 25.5,
          break: 0,
          subBreak: 0,
          unBreak: 0,
          handBreak: 0,
          belt: 1,
          gear: 'D',
          leftLight: 0,
          rightLight: 1,
          nearLight: 1,
          farLight: 0,
          widthLight: 1,
          dangerLight: 0,
          flogLight: 0,
          door: 0,
          button: 0,
          steerWheelAngle: 15.5,
          rmp: 2500,
          averageOil: 8.5,
          leftOil: 45.0,
          totalMileage: 12560.5
        }, null, 2)
      },
      receivedMessages: [],
      qosList: [0, 1, 2],
      client: { connected: false },
      clientReady: false,
      subscribeSuccess: false,
      connecting: false,
      retryTimes: 0,
      connectionError: '',
      latestData: null,
      connectTimer: null
    };
  },
  methods: {
    handleCarIdUpdate(carId) {
      this.carId = carId;
      console.log('Received carId:', this.carId);
      if (this.client && this.client.connected && this.subscribeSuccess) {
        this.doUnSubscribe();
        this.subscription.topic = `vehicle/${this.carId}/up/#`;
        this.doSubscribe();
      }
    },
    handleCarStatus(status) {
      this.carStatus = status;
      if (status) {
        this.scheduleAutoConnect();
      }
    },
    scheduleAutoConnect() {
      if (this.connectTimer || this.connecting || (this.client && this.client.connected)) return;
      const start = () => {
        this.connectTimer = null;
        if (this.carStatus) this.autoConnectAndSubscribe();
      };
      const afterPaint = () => {
        if (typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(() => window.requestAnimationFrame(start));
        } else {
          start();
        }
      };
      this.$nextTick(() => {
        this.connectTimer = setTimeout(afterPaint, 300);
      });
    },
    autoConnectAndSubscribe() {
      const self = this;
      this.connecting = true;
      this.connectionError = '';
      const { protocol, host, port, endpoint, ...options } = this.connection;
      const connectUrl = `${protocol}://${host}${endpoint}`;
      console.log('Auto connecting to:', connectUrl);
      
      this.client = mqtt.connect(connectUrl, options);
      
      if (this.client && typeof this.client.on === 'function') {
        this.client.on('connect', () => {
          self.connecting = false;
          self.client.connected = true;
          self.clientReady = true;
          console.log('Auto connection succeeded!');
          self.doAutoSubscribe();
        });
        this.client.on('reconnect', () => {
          self.handleOnReConnect();
        });
        this.client.on('error', (error) => {
          console.log('Auto connection failed', error);
          self.connectionError = error.message || 'Connection error';
          self.initData();
        });
        this.client.on('close', () => {
          console.log('Connection closed');
          self.initData();
        });
        this.client.on('message', (topic, message) => {
          const msg = {
            time: new Date().toLocaleString(),
            topic: topic,
            payload: message.toString()
          };
          try {
            msg.parsedData = JSON.parse(message.toString());
          } catch (e) {
            msg.parsedData = self.parseSemicolonData(message.toString());
          }
          msg.parsedData = self.normalizeTelemetryData(msg.parsedData);
          self.receivedMessages.unshift(msg);
          if (self.receivedMessages.length > 20) {
            self.receivedMessages.pop();
          }

          //判断车辆ID是否为当前车辆id
          if (!msg.parsedData || (self.carId !== null && msg.parsedData.carId != self.carId)) {
            const messageCarId = msg.parsedData ? msg.parsedData.carId : 'unknown';
            console.log(`Skipping message for carId ${messageCarId}, current carId is ${self.carId}`);
            return;
          }
          self.latestData = msg.parsedData;
          EventBus.$emit('vehicleDataUpdate', msg.parsedData);
          //console.log(`Received message from topic ${ self.carId}`);
        });
      } else {
        throw new Error('Failed to create MQTT client');
      }
    },
    doAutoSubscribe() {
      const self = this;
      const { topic, qos } = this.subscription;
      
      if (!this.client || typeof this.client.subscribe !== 'function') {
        console.error('MQTT client is not properly initialized, cannot subscribe');
        return;
      }
      
      this.client.subscribe(topic, { qos }, (error, res) => {
        if (error) {
          console.log('Auto subscribe error', error);
          return;
        } 
        self.subscribeSuccess = true;
        console.log('Auto subscribe success:', res);
      });
    },
    initData() {
      this.client = { connected: false };
      this.clientReady = false;
      this.retryTimes = 0;
      this.connecting = false;
      this.subscribeSuccess = false;
      this.connectionError = '';
    },
    handleOnReConnect() {
      this.retryTimes += 1;
      if (this.retryTimes > 5) {
        try {
          this.client.end();
          this.initData();
          console.log('Connection maxReconnectTimes limit, stop retry');
        } catch (error) {
          console.error(error.toString());
        }
      }
    },
    handleProtocolChange(value) {
      this.connection.port =  8071;
    },
    createConnection() {
      const self = this;
      try {
        this.connecting = true;
        this.connectionError = '';
        const { protocol, host, port, endpoint, ...options } = this.connection;
        const connectUrl = `${protocol}://${host}:${port}${endpoint}`;
        console.log('Connecting to:', connectUrl);
        
        this.client = mqtt.connect(connectUrl, options);
        
        if (this.client && typeof this.client.on === 'function') {
          this.client.on('connect', () => {
            self.connecting = false;
            self.client.connected = true;
            self.clientReady = true;
            console.log('Connection succeeded!');
          });
          this.client.on('reconnect', () => {
            self.handleOnReConnect();
          });
          this.client.on('error', (error) => {
            console.log('Connection failed', error);
            self.connectionError = error.message || 'Connection error';
            self.initData();
          });
          this.client.on('close', () => {
            console.log('Connection closed');
            self.initData();
          });
          this.client.on('message', (topic, message) => {
            const msg = {
              time: new Date().toLocaleString(),
              topic: topic,
              payload: message.toString()
            };
            try {
              msg.parsedData = JSON.parse(message.toString());
            } catch (e) {
              msg.parsedData = self.parseSemicolonData(message.toString());
            }
            msg.parsedData = self.normalizeTelemetryData(msg.parsedData);
            self.receivedMessages.unshift(msg);
            if (self.receivedMessages.length > 20) {
              self.receivedMessages.pop();
            }
            if (!msg.parsedData) return;
            self.latestData = msg.parsedData;
            EventBus.$emit('vehicleDataUpdate', msg.parsedData);
            console.log(`Received message from topic ${topic}`);
          });
        } else {
          throw new Error('Failed to create MQTT client');
        }
      } catch (error) {
        this.connecting = false;
        this.connectionError = error.message || 'Connection error';
        console.log('mqtt.connect error', error);
      }
    },
    doSubscribe() {
      const self = this;
      if (!this.client || typeof this.client.subscribe !== 'function') {
        console.error('Client is not ready for subscription');
        return;
      }
      const { topic, qos } = this.subscription;
      this.client.subscribe(topic, { qos }, (error, res) => {
        if (error) {
          console.log('Subscribe to topics error', error);
          return;
        }
        self.subscribeSuccess = true;
        console.log('Subscribe to topics res', res);
      });
    },
    doUnSubscribe() {
      const self = this;
      if (!this.client || typeof this.client.unsubscribe !== 'function') {
        console.error('Client is not ready for unsubscription');
        return;
      }
      const { topic } = this.subscription;
      this.client.unsubscribe(topic, (error) => {
        if (error) {
          console.log('Unsubscribe error', error);
        } else {
          self.subscribeSuccess = false;
        }
      });
    },
    doPublish() {
      if (!this.client || typeof this.client.publish !== 'function') {
        console.error('Client is not ready for publishing');
        return;
      }
      const { topic, qos, payload } = this.publish;
      this.client.publish(topic, payload, { qos }, (error) => {
        if (error) {
          console.log('Publish error', error);
        } else {
          console.log('Publish success');
        }
      });
    },
    destroyConnection() {
      const self = this;
      if (this.client && typeof this.client.end === 'function') {
        try {
          this.client.end(false, () => {
            self.initData();
            console.log('Successfully disconnected!');
          });
        } catch (error) {
          console.log('Disconnect failed', error.toString());
        }
      }
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return '-';
      return new Date(timestamp).toLocaleString();
    },
    getSafetyStatus(status) {
      switch (status) {
        case 0: return '正常';
        case 1: return '主动安全刹车';
        case 2: return '主动安全解除';
        default: return '-';
      }
    },
    getHandBreakStatus(status) {
      return status === 1 ? '拉起' : (status === 0 ? '放下' : '-');
    },
    getBeltStatus(status) {
      return status === 1 ? '已系' : (status === 0 ? '未系' : '-');
    },
    getLightStatus(status) {
      return status === 1 ? '打开' : (status === 0 ? '关闭' : '-');
    },
    getDoorStatus(door) {
      if (door === undefined || door === null) return '-';
      const binary = door.toString(2).padStart(4, '0');
      const status = [];
      if (binary[0] === '1') status.push('左后门');
      if (binary[1] === '1') status.push('右后门');
      if (binary[2] === '1') status.push('右前门');
      if (binary[3] === '1') status.push('左前门');
      return status.length > 0 ? status.join('、') + ' 开' : '四门关闭';
    },
    formatNumber(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number.toFixed(1) : '0.0';
    },
    normalizeTelemetryData(data) {
      if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
      const numberOrZero = value => {
        const number = Number(value);
        return Number.isFinite(number) ? number : 0;
      };
      return {
        ...data,
        speed: numberOrZero(data.speed),
        steerWheelAngle: numberOrZero(data.steerWheelAngle),
        oil: numberOrZero(data.oil),
        break: numberOrZero(data.break),
        cluth: numberOrZero(data.cluth)
      };
    },
    parseSemicolonData(data) {
      if (!data || typeof data !== 'string') return null;
      const parts = data.split(';');
      if (parts.length < 32) return null;
      return {
        carId: parseInt(parts[0]) || 0,
        studentId: parseInt(parts[1]) || 0,
        timestamp: parseInt(parts[2]) || 0,
        practiseId: parseInt(parts[3]) || 0,
        x: parseFloat(parts[4]) || 0,
        y: parseFloat(parts[5]) || 0,
        z: parseFloat(parts[6]) || 0,
        longtitude: parseFloat(parts[7]) || 0,
        latitude: parseFloat(parts[8]) || 0,
        height: parseFloat(parts[9]) || 0,
        azimuth: parseFloat(parts[10]) || 0,
        pitchAngle: parseFloat(parts[11]) || 0,
        roll: parseFloat(parts[12]) || 0,
        speed: parseFloat(parts[13]) || 0,
        cluth: parseFloat(parts[14]) || 0,
        oil: parseFloat(parts[15]) || 0,
        break: parseFloat(parts[16]) || 0,
        subBreak: parseFloat(parts[17]) || 0,
        unBreak: parseInt(parts[18]) || 0,
        handBreak: parseInt(parts[19]) || 0,
        belt: parseInt(parts[20]) || 0,
        gear: parts[21] || '-',
        leftLight: parseInt(parts[22]) || 0,
        rightLight: parseInt(parts[23]) || 0,
        nearLight: parseInt(parts[24]) || 0,
        farLight: parseInt(parts[25]) || 0,
        widthLight: parseInt(parts[26]) || 0,
        dangerLight: parseInt(parts[27]) || 0,
        flogLight: parseInt(parts[28]) || 0,
        door: parseInt(parts[29]) || 0,
        button: parseInt(parts[30]) || 0,
        steerWheelAngle: parseFloat(parts[31]) || 0,
        rmp: parts.length > 32 ? parseInt(parts[32]) || 0 : 0,
        averageOil: parts.length > 33 ? parseFloat(parts[33]) || 0 : 0,
        leftOil: parts.length > 34 ? parseFloat(parts[34]) || 0 : 0,
        totalMileage: parts.length > 35 ? parseFloat(parts[35]) || 0 : 0
      };
    }
  }
};
</script>

<style scoped>
.mqtt-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.mqtt-section {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.mqtt-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
  position: relative;
}

.live-indicator {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ff4d4f;
  animation: blink 1s infinite;
  margin-left: 8px;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.form-row {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 150px;
}

.form-group.full-width {
  flex: 1;
  min-width: 100%;
}

.form-group label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.3s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: #1890ff;
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

.btn {
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-success {
  background: #52c41a;
  color: #fff;
}

.btn-success:hover:not(:disabled) {
  background: #389e0d;
}

.btn-danger {
  background: #ff4d4f;
  color: #fff;
}

.btn-danger:hover:not(:disabled) {
  background: #cf1322;
}

.btn-warning {
  background: #faad14;
  color: #fff;
}

.btn-warning:hover:not(:disabled) {
  background: #d48806;
}

.btn-primary {
  background: #1890ff;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: #096dd9;
}

.connection-status {
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
}

.connection-status.disconnected {
  background: #fff2f0;
  color: #cf1322;
}

.connection-status.connected {
  background: #f6ffed;
  color: #52c41a;
}

.connection-error {
  margin-top: 10px;
  padding: 8px 12px;
  background: #fff2f0;
  color: #cf1322;
  border-radius: 4px;
  font-size: 13px;
}

.realtime-panel {
  margin-top: 15px;
}

.panel-row {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.panel-item {
  background: #f5f5f5;
  padding: 12px 16px;
  border-radius: 6px;
  min-width: 140px;
  flex: 1;
}

.item-label {
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 5px;
}

.item-value {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.item-value.speed {
  color: #1890ff;
}

.item-value.status-0 {
  color: #52c41a;
}

.item-value.status-1 {
  color: #ff4d4f;
}

.item-value.status-2 {
  color: #faad14;
}

.empty-message {
  padding: 20px;
  text-align: center;
  color: #999;
  background: #fafafa;
  border-radius: 4px;
}

.receive-container {
  margin-top: 15px;
}

.receive-messages {
  max-height: 400px;
  overflow-y: auto;
}

.message-item {
  background: #f9f9f9;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 10px;
  border-left: 4px solid #1890ff;
}

.msg-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 12px;
}

.msg-time {
  color: #8c8c8c;
}

.msg-topic {
  color: #1890ff;
  font-weight: 500;
}

.msg-parsed {
  font-size: 13px;
}

.msg-payload {
  font-family: monospace;
  font-size: 13px;
  color: #333;
  word-break: break-all;
}

.signal-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.signal-table th,
.signal-table td {
  padding: 6px 10px;
  text-align: left;
  border-bottom: 1px solid #e8e8e8;
}

.signal-table th {
  background: #f5f5f5;
  font-weight: 600;
  color: #666;
}

.signal-table .table-header {
  background: #1890ff;
  color: #fff;
  text-align: center;
}

.signal-table .label {
  color: #8c8c8c;
  width: 35%;
}
</style>
